import React, { useState } from 'react'
import { Modal, Form } from 'semantic-ui-react'
import { AppDropdown as Dropdown } from 'components/AppDropdown'
import { TemplateService, generateTemplatePDFBase64, APIPath } from 'utility'
import { FlexRowBetween } from 'components'
import { SubmitCancelButtons } from 'elements'
import { X } from 'phosphor-react'
import { useNotification } from 'hooks/toast'
import { useTabletAndBelowMediaQuery } from 'layout'

export const SendEmailModal = ({ open, onClose, template, onSuccess }) => {
    const [emailList, setEmailList] = useState([])
    const [loading, setLoading] = useState(false)
    const [pdfGenerating, setPdfGenerating] = useState(false)
    const { successToast } = useNotification()
    const isMobile = useTabletAndBelowMediaQuery()

    const emailOptions = emailList.map(email => ({
        key: email,
        text: email,
        value: email,
    }))

    const handleEmailsChange = (_, { value }) => {
        setEmailList(value)
    }

    const handleEmailsAdd = (_, { value }) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (value && emailRegex.test(value.trim()) && !emailList.includes(value.trim())) {
            setEmailList([...emailList, value.trim()])
        }
    }

    const handleSubmit = async () => {
        // Validate emails
        if (!emailList || emailList.length === 0) {
            return
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        const validEmails = emailList.every(email => emailRegex.test(email))

        if (!validEmails) {
            return
        }

        try {
            setLoading(true)
            setPdfGenerating(true)

            // Use TemplateId (GUID) for API call - API requires GUID format, not numeric Id
            const templateId = template.TemplateId || template.Id
            console.log('📧 Send Email - Template Object:', {
                Id: template.Id,
                TemplateId: template.TemplateId,
                TemplateName: template.TemplateName,
                FullObject: template,
            })
            console.log('📧 Send Email - Using TemplateId for API:', templateId)
            console.log('📧 Send Email - TemplateId type:', typeof templateId)
            console.log('📧 Send Email - API URL will be:', `${APIPath.templeteDetails}${templateId}`)

            // Fetch full template data to generate PDF
            console.log('📧 Send Email - Fetching template details from API...')
            const templateData = await TemplateService.getTemplateDetails(templateId)
            console.log('📧 Send Email - Template Data Received:', {
                HasData: !!templateData,
                Id: templateData?.Id,
                TemplateId: templateData?.TemplateId,
                TemplateName: templateData?.TemplateName,
                SectionsCount: templateData?.Sections?.length || 0,
                FullData: templateData,
            })

            if (!templateData || !templateData.Id) {
                console.error('📧 Send Email - Failed to fetch template data:', templateData)
                setPdfGenerating(false)
                setLoading(false)
                return
            }

            // Generate PDF as Base64
            console.log('📧 Send Email - Generating PDF Base64...')
            const pdfBase64 = generateTemplatePDFBase64(templateData)
            console.log('📧 Send Email - PDF Generated:', {
                pdfBase64Length: pdfBase64 ? pdfBase64.length : 0,
                pdfBase64Preview: pdfBase64 ? `${pdfBase64.substring(0, 50)}...` : null,
            })

            if (!pdfBase64) {
                console.error('📧 Send Email - PDF generation returned null/empty')
                setPdfGenerating(false)
                setLoading(false)
                return
            }

            setPdfGenerating(false)

            // Prepare email data - join emails with semicolon
            const emailsString = emailList.join(';')
            const emailPayload = {
                Emails: emailsString,
                TemplateId: templateId,
                TemplateName: template.TemplateName,
                PdfBase64: pdfBase64,
            }

            console.log('📧 Send Email - Sending to Backend:', {
                Emails: emailPayload.Emails,
                TemplateId: emailPayload.TemplateId,
                TemplateName: emailPayload.TemplateName,
                PdfBase64Length: emailPayload.PdfBase64.length,
                PdfBase64Preview: `${emailPayload.PdfBase64.substring(0, 50)}...`,
            })

            // Send email with PDF attachment
            await TemplateService.sendTemplateEmailWithPDF(emailPayload)

            successToast('Email sent successfully')
            if (onSuccess) onSuccess()
            setEmailList([])
            onClose()
        } catch (error) {
            console.error('Error sending email:', error)
        } finally {
            setLoading(false)
            setPdfGenerating(false)
        }
    }

    const getSubmitButtonText = () => {
        if (pdfGenerating) {
            return 'Generating PDF...'
        }
        if (loading) {
            return 'Sending Email...'
        }
        return 'Send Email'
    }

    return (
        <Modal open={open} onClose={onClose} size="tiny" className="model">
            <Modal.Header>
                <FlexRowBetween>
                    <div>
                        <div>Send Email Template PDF</div>
                        <div style={{ fontSize: '0.875rem', color: '#666', marginTop: '4px' }}>
                            {template?.TemplateName}
                        </div>
                    </div>
                    <X className="pointer" size={32} onClick={onClose} />
                </FlexRowBetween>
            </Modal.Header>
            <Modal.Content>
                <Form>
                    <Form.Field>
                        <label>Email Addresses</label>
                        <Dropdown
                            placeholder="Type email and press Enter"
                            fluid
                            multiple
                            search
                            selection
                            allowAdditions
                            value={emailList}
                            options={emailOptions}
                            onChange={handleEmailsChange}
                            onAddItem={handleEmailsAdd}
                            icon={null}
                            noResultsMessage={null}
                        />
                    </Form.Field>
                </Form>
            </Modal.Content>
            <Modal.Actions>
                <div className={isMobile ? 'mb-3' : ''}>
                    <SubmitCancelButtons
                        loading={loading || pdfGenerating}
                        onSubmit={handleSubmit}
                        onCancel={onClose}
                        hideCancel={false}
                        cancelText="Cancel"
                        submitText={getSubmitButtonText()}
                        className="mt-2"
                    />
                </div>
            </Modal.Actions>
        </Modal>
    )
}

export default SendEmailModal
